import React from "react";
import AdminPageLayout from "../../../components/FullPageLayout/AdminPageLayout";
import { Box, IconButton } from "@mui/material";
import { useDispatch } from "react-redux";
import { push } from "connected-react-router";
import ReactTable from "../../../components/ReactTable/Table";
import { useTranslation } from "react-i18next";
import { buildManageRespositoryRoute } from "../../../routes/AdminRoutes";
import { useRepositories } from "../../../hooks/repositories";
import PushPinIcon from "../../../components/Icons/PushPinIcon";
import { useRepositoryPins } from "../../../hooks/repositoryPins";
import makeStyles from "@mui/styles/makeStyles";
import { usePinnedRows } from "../../../components/ReactTable/usePinnedRows";
import useUser from "../../../hooks/useUser";
import Tooltip from "../../../components/Tooltip/Tooltip";

const useStyles = makeStyles(theme => ({
  pinnedRepositoryRow: {
    backgroundColor: theme.palette.action.hover,
    "& td": {
      borderBottomColor: theme.palette.primary.main
    }
  }
}));

const getInitialPin = (row, initialRepositoryPins) => {
  const initialPin =
    initialRepositoryPins &&
    initialRepositoryPins.find(
      repositoryPin =>
        repositoryPin.library.id === `${row.contextId}` &&
        repositoryPin.libraryType === row.contextType
    );
  return !!initialPin ? initialPin.id : null;
};

const PinRepositoryCell = ({
  row,
  isPinned,
  isPinUpdating,
  onPinRepository
}) => {
  const { t } = useTranslation();
  return (
    <Box
      display="flex"
      className={!isPinned ? "hover-action" : ""}
      data-qa="pin-repository-cell"
    >
      <Tooltip
        title={t(
          isPinned ? "repositories.unpinBtn" : "repositories.pinToTopBtn"
        )}
      >
        <div>
          <IconButton
            disabled={isPinUpdating}
            aria-label={t(
              isPinned ? "repositories.unpinBtn" : "repositories.pinToTopBtn"
            )}
            onClick={event => onPinRepository(event, row)}
            color={!isPinned ? "default" : "primary"}
            data-qa="pin-repository-btn"
            size="large"
          >
            <PushPinIcon />
          </IconButton>
        </div>
      </Tooltip>
    </Box>
  );
};

const columns = (t, onPinRepository, isRowPinned, isPinUpdating) => [
  {
    Header: t("table.columnHeader.repository"),
    accessor: "name",
    id: "name"
  },
  {
    Header: t("table.columnHeader.files"),
    accessor: "totalItems",
    id: "totalItems"
  },
  {
    Header: t("table.columnHeader.owner"),
    accessor: "customerName",
    id: "customerName"
  },
  {
    disableSortBy: true,
    accessor: "actions",
    Cell: ({ row }) => (
      <PinRepositoryCell
        row={row}
        isPinned={isRowPinned(row.id)}
        isPinUpdating={isPinUpdating(row.id)}
        onPinRepository={onPinRepository}
      />
    )
  }
];

const AllRepositories = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentUser = useUser();
  const {
    createPinnedRepository,
    deletePinnedRepository,
    repositoryPins: initialRepositoryPins,
    isLoadingPins
  } = useRepositoryPins();

  const { repositories, isLoading } = useRepositories();
  const { pinRow, sortRows, isRowPinned, isPinUpdating } = usePinnedRows(
    repositories,
    initialRepositoryPins,
    getInitialPin
  );

  const handleRepositoryClick = repositoryData => {
    dispatch(push(buildManageRespositoryRoute(repositoryData.id)));
  };

  const handlePinRepository = async (event, row) => {
    event.stopPropagation();
    pinRow(
      row,
      () =>
        createPinnedRepository({
          library: {
            type: row.original.contextType,
            id: row.original.contextId
          },
          user: {
            type: "user",
            id: currentUser.id
          }
        }),
      deletePinnedRepository
    );
  };

  return (
    <AdminPageLayout title={t("repositories.title")}>
      <Box my={4}>
        <ReactTable
          columns={columns(t, handlePinRepository, isRowPinned, isPinUpdating)}
          data={repositories}
          manualSortBy={false}
          onRowClick={rowData => handleRepositoryClick(rowData)}
          getRowClass={({ original: { id } }) =>
            isRowPinned(id) ? classes.pinnedRepositoryRow : null
          }
          orderByFn={sortRows}
          initialState={{ sortBy: [{ id: "name" }] }}
          autoResetRowState={false}
          loading={isLoading || isLoadingPins}
        />
      </Box>
    </AdminPageLayout>
  );
};

export default AllRepositories;

AllRepositories.propTypes = {};
